import React from 'react'
import { classNames } from 'common/emotion'
import { AmpImg } from 'component-library/content/Picture/Image.amp'
import { StyledPicture } from 'component-library/content/Picture/Picture.styled'
import { Props } from 'component-library/content/Picture/Picture'
import { getSrcSetValue, ImageSrcInfo } from './layouts/helpers/srcSet'
import { ImageWidth } from 'common/helpers/image'
import { scaleImage } from 'common/helpers/scaleImage'
import { getCrops, fixedRatioToBreakpointRatios } from './utils'
import { useProduct } from 'common/hooks/useProduct'

const AmpStyledPicture = (StyledPicture as any).withComponent('div') as typeof StyledPicture

export const AmpPicture: React.FC<Props> = props => {
    const product = useProduct()
    const { image, layout, fixedRatio } = props
    const renderPicture = props.renderPicture
        ? props.renderPicture
        : (pic: React.ReactElement) => pic

    if (layout === 'placeholder' || !image) {
        // Required because of Error being thrown
        throw new Error('Image dimensions must be specified when using amp')
    }

    if (!fixedRatio) {
        return null
    }

    const getScaledSrc = (src: string, width: ImageWidth) => scaleImage(src, width, false)

    const breakpointCrops = getCrops(image.crops, fixedRatioToBreakpointRatios(fixedRatio), false)

    if (!breakpointCrops) {
        return null
    }

    const crop = breakpointCrops.default

    const srcInfo: ImageSrcInfo = {
        product,
        srcCallback: getScaledSrc,
        // TODO this cast does not make sense?
        initialWidth: crop.width as ImageWidth,
    }

    const srcSetValue = getSrcSetValue(srcInfo, crop)

    return renderPicture(
        <AmpStyledPicture
            className={classNames(props.className, 'image')}
            loadingBackgroundColor={props.loadingBackgroundColor}
        >
            {/** We are not going to set sizes={image.sizes} as affects the size of the image being displayed */}
            <AmpImage
                src={crop.reference}
                srcSet={srcSetValue}
                alt={image.altText}
                width={crop.width}
                height={crop.height}
            />
        </AmpStyledPicture>
    )
}

export const AmpImage = (props: {
    src: string
    srcSet?: string
    alt?: string
    width: number
    height: number
    sizes?: string
    layout?: 'fill' | 'responsive' | undefined
}) => {
    const { src, srcSet, alt, width, height, sizes, layout = 'responsive' } = props

    return src.length > 0 ? (
        <AmpImg
            src={src}
            srcSet={srcSet}
            alt={alt}
            width={width}
            height={height}
            sizes={sizes}
            layout={layout}
        >
            <noscript>
                <img src={src} alt={alt} /> {/* src set not needed on amp noscript */}
            </noscript>
        </AmpImg>
    ) : null
}
AmpPicture.displayName = 'AmpPicture'
